/**
 * JobsTable — reusable Jobs list as a DataTable.
 *
 * Encapsulates the full toolbar (search + status filter chips + optional
 * member filter) and the columns so callers don't repeat themselves.
 *
 * Two consumers today:
 *   - /jobs (Jobs list page)  — shows the Assigned column + member filter
 *   - Member detail → Work history tab  — hides both (already scoped to one member)
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Avatar } from '@/components/Avatar/Avatar';
import { Alert } from '@/components/Alert/Alert';
import { FilterChip } from '@/components/FilterChip/FilterChip';
import { createColumnHelper, DataTable, type ColumnDef } from '@/components/DataTable/DataTable';
import { t } from '@/lib/i18n';
import { formatRelativeTime } from '@/lib/format';
import type { JobListItem, JobStatus } from '../schemas';
import { JobStatusBadge } from './JobStatusBadge';
import styles from './JobsTable.module.css';

type StatusFilter = 'all' | JobStatus;
const ALL = '__all__';

const columnHelper = createColumnHelper<JobListItem>();

export interface JobsTableProps {
  data: readonly JobListItem[];
  isLoading?: boolean;
  isError?: boolean;
  /** Hide the Assigned column + member filter — useful inside a per-member view. */
  showAssignedColumn?: boolean;
  /** Right-aligned slot in the toolbar (typically a "+ New Job" button). */
  toolbarActions?: ReactNode;
  /** Empty-state content when data is empty. */
  emptyState?: ReactNode;
}

const STATUS_CHIPS: readonly { key: StatusFilter; labelKey: string; status: JobStatus | null }[] = [
  { key: 'all', labelKey: 'All', status: null },
  { key: 'running', labelKey: 'Running', status: 'running' },
  { key: 'completed', labelKey: 'Done', status: 'completed' },
  { key: 'failed', labelKey: 'Failed', status: 'failed' },
];

export function JobsTable({
  data,
  isLoading,
  isError,
  showAssignedColumn = true,
  toolbarActions,
  emptyState,
}: JobsTableProps) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [memberFilter, setMemberFilter] = useState<string>(ALL);

  // Apply member filter first so the status-chip counts reflect "within the
  // selected member". The Jobs page lets users narrow by member; work-history
  // never shows this dropdown so the value stays ALL.
  const memberFiltered = useMemo(
    () =>
      memberFilter === ALL
        ? (data as JobListItem[])
        : data.filter((j) => j.assigned_member.id === memberFilter),
    [data, memberFilter],
  );

  const counts: Record<StatusFilter, number> = useMemo(
    () => ({
      all: memberFiltered.length,
      running: memberFiltered.filter((j) => j.status === 'running').length,
      awaiting_input: memberFiltered.filter((j) => j.status === 'awaiting_input').length,
      completed: memberFiltered.filter((j) => j.status === 'completed').length,
      failed: memberFiltered.filter((j) => j.status === 'failed').length,
      cancelled: memberFiltered.filter((j) => j.status === 'cancelled').length,
    }),
    [memberFiltered],
  );

  const tableData = useMemo(() => {
    if (statusFilter === 'all') return memberFiltered;
    return memberFiltered.filter((j) => j.status === statusFilter);
  }, [memberFiltered, statusFilter]);

  // Members referenced in the unfiltered data — for the assigned-member dropdown.
  const memberOptions = useMemo(() => {
    const map = new Map<string, string>();
    data.forEach((j) => map.set(j.assigned_member.id, j.assigned_member.display_name));
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  const columns: ColumnDef<JobListItem, unknown>[] = useMemo(() => {
    const base: ColumnDef<JobListItem, unknown>[] = [
      columnHelper.accessor((j) => j.title, {
        id: 'title',
        header: t('Title'),
        cell: (info) => {
          const job = info.row.original;
          return (
            <Link to={`/jobs/${job.id}`} className={styles.titleLink}>
              {job.title}
            </Link>
          );
        },
      }),
      columnHelper.accessor((j) => j.status, {
        id: 'status',
        header: t('Status'),
        cell: (info) => <JobStatusBadge status={info.getValue()} />,
        sortingFn: 'basic',
      }),
    ];

    if (showAssignedColumn) {
      base.push(
        columnHelper.accessor((j) => j.assigned_member.display_name, {
          id: 'assigned',
          header: t('Assigned to'),
          cell: (info) => {
            const member = info.row.original.assigned_member;
            return (
              <span className={styles.assigned}>
                <Avatar name={member.display_name} size="sm" />
                <span>{member.display_name}</span>
              </span>
            );
          },
        }),
      );
    }

    base.push(
      columnHelper.accessor((j) => Date.parse(j.created_at), {
        id: 'created',
        header: t('Created'),
        cell: (info) => formatRelativeTime(info.row.original.created_at),
      }),
      columnHelper.accessor((j) => durationMs(j) ?? -1, {
        id: 'duration',
        header: t('Duration'),
        cell: (info) => durationLabel(info.row.original),
      }),
    );

    return base;
  }, [showAssignedColumn]);

  const searchAccessor = showAssignedColumn
    ? (row: JobListItem) => `${row.title} ${row.assigned_member.display_name}`
    : (row: JobListItem) => row.title;

  if (isError) {
    return <Alert variant="error">{t("Couldn't load Jobs.")}</Alert>;
  }

  return (
    <DataTable
      data={tableData}
      columns={columns}
      ariaLabel={t('Jobs')}
      defaultSort={{ id: 'created', desc: true }}
      isLoading={isLoading}
      emptyState={
        statusFilter === 'all' && memberFilter === ALL
          ? (emptyState ?? t('No Jobs yet.'))
          : t('No Jobs match these filters.')
      }
      searchPlaceholder={t('Search Jobs…')}
      searchAccessor={searchAccessor}
      filterChips={
        <>
          {STATUS_CHIPS.map((chip) => (
            <FilterChip
              key={chip.key}
              label={t(chip.labelKey)}
              count={counts[chip.key]}
              isActive={statusFilter === chip.key}
              onClick={() => setStatusFilter(chip.key)}
            />
          ))}
          {showAssignedColumn && memberOptions.length > 1 ? (
            <label className={styles.memberFilter}>
              <span className={styles.memberFilterLabel}>{t('Member')}</span>
              <select
                className={styles.memberSelect}
                value={memberFilter}
                onChange={(e) => setMemberFilter(e.target.value)}
              >
                <option value={ALL}>{t('All')}</option>
                {memberOptions.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </>
      }
      toolbarActions={toolbarActions}
    />
  );
}

/* ─── Helpers ────────────────────────────────────────────────────── */

function durationMs(j: JobListItem): number | null {
  if (j.completed_at && j.started_at) {
    return Date.parse(j.completed_at) - Date.parse(j.started_at);
  }
  if (j.started_at && j.status === 'running') {
    return Date.now() - Date.parse(j.started_at);
  }
  return null;
}

function durationLabel(j: JobListItem): string {
  const ms = durationMs(j);
  if (ms === null) return '—';
  const formatted = formatDurationMs(ms);
  return j.status === 'running' ? t('Running {d}', { d: formatted }) : formatted;
}

function formatDurationMs(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m`;
  return `${(ms / 3_600_000).toFixed(1)}h`;
}
